import { ReportImageInfo } from '@barthauer/abc/core/data';

const InchInMM: number = 25.4;
export class PrintTemplate {
  public picBoxWidthInMM: number;
  public picBoxHeightInMM: number;
  public pageWidthInMM?: number;
  public pageHeightInMM?: number;
  public isBackColorTransparent?: boolean;
  public picBoxBandHeightInMM?: number;
  public picBoxLeftInMM?: number;
  public picBoxTopInMM?: number;
  public pageLeftMarginInMM?: number;
  public pageRightMarginInMM?: number;
  public pageTopMarginInMM?: number;
  public pageBottomMarginInMM?: number;

  public allScales: PrintScale[];
  public allResolutions: PrintResolution[];

  public get validResolutions(): PrintResolution[] {
    return this.allResolutions.filter((r) => r.isValidCanvasSize);
  }
  public get validScales(): PrintScale[] {
    return this.allScales.filter((s) => s.isValid);
  }
  public get picBoxWidthInInch(): number {
    return this.picBoxWidthInMM / InchInMM;
  }
  public get picBoxHeightInInch(): number {
    return this.picBoxHeightInMM / InchInMM;
  }
  public get picBoxLeftPosInInch(): number {
    return (this.pageLeftMarginInMM + this.picBoxLeftInMM) / InchInMM;
  }
  public get picBoxBottomPosInInch(): number {
    return (this.pageHeightInMM - (this.pageTopMarginInMM + this.picBoxTopInMM + this.picBoxHeightInMM)) / InchInMM;
  }

  constructor(
    public id: string,
    public name: string,
    public opath: string,
    imageInfo: ReportImageInfo,
    predefinedScales: number[],
    definedResolutions: number[],
    public allowRotation: boolean
  ) {
    this.picBoxWidthInMM = imageInfo.WidthInMM;
    this.picBoxHeightInMM = imageInfo.HeightInMM;
    this.pageWidthInMM = imageInfo.PageWidthInMM;
    this.pageHeightInMM = imageInfo.PageHeightInMM;
    this.isBackColorTransparent = imageInfo.IsBackColorTransparent;
    this.picBoxBandHeightInMM = imageInfo.BandHeightInMM;
    this.picBoxLeftInMM = imageInfo.LeftInMM;
    this.picBoxTopInMM = imageInfo.TopInMM;
    this.pageLeftMarginInMM = imageInfo.LeftMarginInMM;
    this.pageRightMarginInMM = imageInfo.RightMarginInMM;
    this.pageTopMarginInMM = imageInfo.TopMarginInMM;
    this.pageBottomMarginInMM = imageInfo.BottomMarginInMM;

    this.allScales = new Array<PrintScale>();
    predefinedScales = predefinedScales.sort((a, b) => a - b);
    this.addScales(predefinedScales);
    if (definedResolutions && definedResolutions.length > 0) {
      this.allResolutions = new Array<PrintResolution>();
      for (const dpi of definedResolutions) {
        this.addResolution(dpi);
      }
    } else {
      this.allResolutions = new Array<PrintResolution>();
      this.addResolution(150);
      this.addResolution(300);
      this.addResolution(600);
      this.addResolution(1200);
    }
  }

  public getNearestScale(targetScale: number): PrintScale {
    let result: PrintScale;
    if (this.validScales.length > 1) {
      let lastDistance: number = Math.abs(this.validScales[0].value - targetScale);

      for (let i = 0; i < this.validScales.length; i++) {
        const scale = this.validScales[i].value;
        const distance = Math.abs(scale - targetScale);
        if (lastDistance < distance) {
          result = this.validScales[i - 1];
          break;
        } else if (distance === 0) {
          result = this.validScales[i];
          break;
        }
        lastDistance = distance;
      }
    } else {
      result = this.validScales[0];
    }

    return result;
  }

  private addScales(predefinedScales: number[]) {
    for (let i = 0; i < predefinedScales.length; i++) {
      const scale: number = predefinedScales[i];

      const size: number[] = this.getPrintSizeInMeters(scale);
      let isValid: boolean = size[0] <= 2000 && size[1] <= 2000;
      const printScale: PrintScale = new PrintScale(scale, isValid);
      this.allScales.push(printScale);
    }
  }

  public getPrintSizeInMeters(scale: number): number[] {
    return [(this.picBoxWidthInMM / 1000) * scale, (this.picBoxHeightInMM / 1000) * scale];
  }

  private addResolution(dpi: number) {
    const widthPixel: number = this.picBoxWidthInInch * dpi;
    const heightPixel: number = this.picBoxHeightInInch * dpi;
    const isValidCanvasSize: boolean = this.getIsValidCanvasSize(widthPixel, heightPixel);
    const resolution: PrintResolution = new PrintResolution(widthPixel, heightPixel, dpi, isValidCanvasSize);

    this.allResolutions.push(resolution);
  }

  //https://developer.mozilla.org/en-US/docs/Web/HTML/Element/canvas
  private getIsValidCanvasSize(widthPixel: number, heightPixel: number): boolean {
    const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isChromium: boolean = navigator.userAgent.indexOf('Chrome') != -1;
    const isFirefox: boolean = navigator.userAgent.indexOf('Firefox') != -1;

    let maxXorY: number = Number.MAX_SAFE_INTEGER;
    let maxArea: number = Number.MAX_SAFE_INTEGER;
    if (isChromium || isSafari) {
      maxXorY = 32767;
      maxArea = 268435456;
    } else if (isFirefox) {
      maxXorY = 32767;
      maxArea = 472907776;
    }
    const areaPixel: number = widthPixel * heightPixel;
    return widthPixel <= maxXorY && heightPixel <= maxXorY && areaPixel <= maxArea;
  }
}
export class PrintResolution {
  public get displayText(): string {
    return this.dpi + ' dpi';
  }
  constructor(
    private widthPixel: number,
    private heightPixel: number,
    public dpi: number,
    public isValidCanvasSize: boolean
  ) {}
}

export class PrintScale {
  public get displayText(): string {
    return '1:' + this.value.toString();
  }
  constructor(
    public value: number,
    public isValid: boolean
  ) {}
}

export class PrintTemplateSelector {
  public get displayText(): string {
    return this.printTemplate?.name ?? this.name;
  }
  constructor(
    public name: string,
    public action: () => void,
    public printTemplate: PrintTemplate
  ) {}
}

export class PrintSettingsStorage {
  selectedId: string;
  templates: PrintTemplate[];
  constructor() {
    this.templates = new Array<PrintTemplate>();
  }
}
